import React, { useEffect } from 'react';
import { withRouter } from 'react-router';
import { Translation, getI18n } from 'react-i18next';
import { Table } from 'antd';
import ListActions from '../../../elements/components/ListActions';
import {
  StatusTag,
  CognitoFormTypeTag,
  CognitoFormPublicStatusTag,
} from '../../../elements/components/Tags';
import {
  getColumnSearchProps,
  getColumnDateRangeProps,
} from '../../../elements/components/TableColumnFilters';
import { pathTo } from '../../../Routes';
import Format from '../../../../../lib/Format';
import QueryString from '../../../../../lib/QueryString';
import Logger from '../../../../../lib/Logger';

const CognitoFormsList = ({
  component,
  page,
  limit,
  order,
  filter,
  total,
  load,
  remove,
  history,
  ...props
}) => {
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };

  const handleReset = (clearFilters, dataIndex) => {
    clearFilters();
    delete filter[dataIndex];
    history.push(
      QueryString.append(props.location.pathname, { order, ...filter })
    );
  };

  const columns = [
    {
      title: getI18n().t('cognito_form_registration_code'),
      dataIndex: 'code',
      key: 'code',
      ...getColumnSearchProps(
        'code',
        handleSearch,
        handleReset,
        getI18n().t('cognito_form_registration_code'),
        filter
      ),
    },
    {
      title: getI18n().t('cognito_form_registration_code_alias'),
      dataIndex: 'code_alias',
      key: 'code_alias',
      ...getColumnSearchProps(
        'code_alias',
        handleSearch,
        handleReset,
        getI18n().t('cognito_form_registration_code_alias'),
        filter
      ),
    },
    {
      title: getI18n().t('cognito_form_name'),
      dataIndex: 'name',
      key: 'name',
      ...getColumnSearchProps(
        'name',
        handleSearch,
        handleReset,
        getI18n().t('cognito_form_name'),
        filter
      ),
    },
    {
      title: getI18n().t('cognito_form_form_id'),
      dataIndex: 'form_id',
      key: 'form_id',
      ...getColumnSearchProps(
        'form_id',
        handleSearch,
        handleReset,
        getI18n().t('cognito_form_form_id'),
        filter
      ),
    },
    {
      title: getI18n().t('cognito_form_form_key'),
      dataIndex: 'form_key',
      key: 'form_key',
      ...getColumnSearchProps(
        'form_key',
        handleSearch,
        handleReset,
        getI18n().t('cognito_form_form_key'),
        filter
      ),
    },
    {
      title: getI18n().t('cognito_form_admin_created_by'),
      dataIndex: 'profile_name',
      key: 'profile_name',
    },
    {
      title: getI18n().t('cognito_form_type'),
      dataIndex: 'type',
      key: 'type',
      filters: [
        {
          text: getI18n().t('cognito_form_type_onboarding'),
          value: 1,
        },
        {
          text: getI18n().t('cognito_form_type_internal'),
          value: 2,
        },
        {
          text: getI18n().t('cognito_form_type_survey'),
          value: 3,
        },
      ],
      filteredValue: 'type' in filter ? filter['type'].split(',') : null,
      render: (type) => <CognitoFormTypeTag type={type} />,
    },
    {
      title: getI18n().t('cognito_form_public_status'),
      dataIndex: 'public_status',
      key: 'public_status',
      filters: [
        {
          text: getI18n().t('cognito_form_public_status_active'),
          value: 1,
        },
        {
          text: getI18n().t('cognito_form_public_status_inactive'),
          value: 2,
        },
      ],
      filteredValue:
        'public_status' in filter ? filter['public_status'].split(',') : null,
      render: (public_status) => (
        <CognitoFormPublicStatusTag public_status={public_status} />
      ),
    },
    {
      title: getI18n().t('table_header_status'),
      dataIndex: 'status',
      key: 'status',
      filters: [
        { text: getI18n().t('status_enabled'), value: 1 },
        { text: getI18n().t('status_disabled'), value: 2 },
        { text: getI18n().t('status_archived'), value: 3 },
        { text: getI18n().t('status_deleted'), value: 4 },
        { text: getI18n().t('status_pending'), value: 5 },
      ],
      filteredValue: 'status' in filter ? filter['status'].split(',') : null,
      render: (code) => <StatusTag status={code} />,
    },
    {
      title: getI18n().t('table_header_updated_at'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (date) => Format.date(date),
      defaultSortOrder:
        order === 'updated_at.asc'
          ? 'ascend'
          : order === 'updated_at.desc'
          ? 'descend'
          : null,
      sorter: true,
      ...getColumnDateRangeProps(
        'updated_at',
        handleSearch,
        handleReset,
        filter
      ),
    },
    {
      title: getI18n().t('table_header_created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => Format.date(date),
      defaultSortOrder:
        order === 'created_at.asc'
          ? 'ascend'
          : order === 'created_at.desc'
          ? 'descend'
          : null,
      sorter: true,
      ...getColumnDateRangeProps(
        'created_at',
        handleSearch,
        handleReset,
        filter
      ),
    },
    {
      title: getI18n().t('table_header_actions'),
      key: 'actions',
      render: (item) => (
        <ListActions
          id={item.id}
          page={page}
          limit={limit}
          order={order}
          filter={filter}
          load={load.bind(this)}
          remove={remove.bind(this)}
          editScreen='CognitoFormEditScreen'
        />
      ),
    },
  ];

  const pagination = {
    current: page,
    pageSize: limit,
    total: total,
    showSizeChanger: false,
    position: ['bottomCenter'],
  };

  const handleTableChange = (pagination, filters, sorter) => {
    let path = props.location.pathname;
    const params = {};

    // handle pagination
    if ('current' in pagination && pagination['current']) {
      path = pathTo(component, { page: pagination['current'] });
    }

    // handle sorting
    if ('field' in sorter && 'order' in sorter) {
      if (sorter['field'] && sorter['order']) {
        const order =
          sorter['field'] +
          '.' +
          (sorter['order'] === 'ascend' ? 'asc' : 'desc');
        params['order'] = order;
      }
    }

    // handle filters
    if (filters) {
      for (const key in filters) {
        if (filters[key]) {
          params[key] = filters[key].join(',');
        }
      }
    }

    history.push(QueryString.append(path, params));
  };

  const filterString = JSON.stringify(filter);
  useEffect(() => {
    load(page, limit, order, JSON.parse(filterString));
  }, [page, limit, order, filterString, load]);

  return (
    <Translation>
      {(t) => (
        <Table
          dataSource={props.list}
          columns={columns}
          pagination={page === 1 && total <= limit ? false : pagination}
          loading={props.isLoading}
          scroll={{ x: true }}
          onChange={handleTableChange}
        />
      )}
    </Translation>
  );
};

export default withRouter(CognitoFormsList);

Logger.log('silly', `CognitoFormsList loaded.`);
